import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import {
  normalizeScenarioProfile,
  SCENARIO_PROFILE_OPTIONS,
} from '../../constants/cctv';

/** 시설 전체화면 오버레이(zIndex 1800)보다 위 */
export const RISK_SIMULATE_DIALOG_Z = 2000;

type CctvOption = { id: number; name: string; detection_scenario?: string | null };

type Props = {
  cctvOptions: CctvOption[];
  scenarioProfile?: string | null;
  /** CCTV만 고르면 곧바로 실제 이벤트 창을 연다 */
  onPickCctv: (cctvId: number) => void;
};

/** CentralMonitoring — 이벤트: CCTV 선택 후 바로 이벤트 창 */
const RiskSimulateBar: React.FC<Props> = ({
  cctvOptions,
  scenarioProfile,
  onPickCctv,
}) => {
  const [manualOpen, setManualOpen] = useState(false);
  const [selectedCctvId, setSelectedCctvId] = useState<string>('');
  const hasOptions = cctvOptions.length > 0;

  const parsedCctvId = useMemo(() => {
    const n = Number(selectedCctvId);
    return Number.isFinite(n) && n > 0 ? n : undefined;
  }, [selectedCctvId]);

  const profileNorm = normalizeScenarioProfile(scenarioProfile);
  const profileLabel =
    SCENARIO_PROFILE_OPTIONS.find((o) => o.value === profileNorm)?.label || profileNorm;

  const openDialog = () => {
    setSelectedCctvId(cctvOptions.length === 1 ? String(cctvOptions[0].id) : '');
    setManualOpen(true);
  };

  const closeDialog = () => setManualOpen(false);

  const goNext = () => {
    if (parsedCctvId == null) return;
    closeDialog();
    onPickCctv(parsedCctvId);
  };

  const selectMenuProps = {
    disablePortal: true,
    PaperProps: { sx: { maxHeight: 320 } },
  } as const;

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        color="warning"
        onClick={openDialog}
        sx={{ fontSize: '0.75rem', px: 1.2, py: 0.25, minWidth: 'auto', height: 30 }}
      >
        이벤트
      </Button>

      <Dialog
        open={manualOpen}
        onClose={closeDialog}
        maxWidth="xs"
        fullWidth
        sx={{ zIndex: RISK_SIMULATE_DIALOG_Z }}
      >
        <DialogTitle>이벤트 · CCTV 선택</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" color="text.secondary">
              CCTV를 고른 뒤 다음을 누르면 이벤트 창이 열림. 코드명·유형은 그 창에서
              입력
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="risk-sim-cctv-label">CCTV</InputLabel>
              <Select
                labelId="risk-sim-cctv-label"
                label="CCTV"
                value={selectedCctvId}
                onChange={(e) => setSelectedCctvId(String(e.target.value))}
                MenuProps={selectMenuProps}
              >
                {cctvOptions.map((c) => (
                  <MenuItem key={c.id} value={String(c.id)}>
                    {c.name?.trim() || `CCTV #${c.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {!hasOptions ? (
              <Typography variant="caption" color="warning.main">
                이 시설에 선택 가능한 CCTV가 없음
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">
                시설 프로필: {profileLabel}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>닫기</Button>
          <Button
            variant="contained"
            disabled={!hasOptions || parsedCctvId == null}
            onClick={goNext}
          >
            다음
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default RiskSimulateBar;
